import { useEffect, useState } from 'react'
import { OpenNewWindow as ExternalIcon } from 'iconoir-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { useSelectedWorkspace } from '@/hooks/useSelectedWorkspace'
import { useRole } from '@/hooks/useRole'
import { externalAddress } from '@/config/nav-groups'
import { buildStripModel, shellStatusStoreFor, UNKNOWN_STATUS } from '@/lib/shell-status-client'
import { heldStoreFor } from '@/components/engine/unmatched/held-store'
import { applyHeldToStrip } from '@/components/engine/unmatched/strip-model'

const TONES = {
  success: 'bg-(--success)/10 text-(--success)',
  warning: 'bg-(--warning)/10 text-(--warning)',
  error: 'bg-(--error)/10 text-(--error)',
  neutral: 'bg-(--muted) text-(--muted-foreground)',
  link: 'text-(--muted-foreground) hover:text-(--accent)',
}

function useShellStatus() {
  const { getToken, user, sessionId, isAuthenticated } = useAuth()
  const workspaceId = useSelectedWorkspace()
  const [slot, setSlot] = useState({ workspaceId: null, loading: true, status: UNKNOWN_STATUS })
  const setState = next => setSlot({ workspaceId, ...next })
  const state = slot.workspaceId === workspaceId ? slot : { loading: true, status: UNKNOWN_STATUS }
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_ENGINE_API_URL
    if (!isAuthenticated || !user?.id || !sessionId || !baseUrl) {
      setState({ loading: false, status: UNKNOWN_STATUS })
      return undefined
    }
    let live = true
    shellStatusStoreFor({ baseUrl, getToken, userId: user.id, sessionId, allowLocal: import.meta.env.DEV, workspaceId })
      .read()
      .then(status => { if (live) setState({ loading: false, status }) })
    return () => { live = false }
  }, [getToken, user?.id, sessionId, isAuthenticated, workspaceId])
  return state
}

// The held reply summary, from the same call the Unmatched replies screen makes.
function useHeldReplies() {
  const { getToken, user, sessionId, isAuthenticated } = useAuth()
  const workspaceId = useSelectedWorkspace()
  const [slot, setSlot] = useState({ workspaceId: null, loading: true, held: null })
  const setState = next => setSlot({ workspaceId, ...next })
  const state = slot.workspaceId === workspaceId ? slot : { loading: true, held: null }
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_ENGINE_API_URL
    if (!isAuthenticated || !user?.id || !sessionId || !baseUrl) {
      setState({ loading: false, held: null })
      return undefined
    }
    let live = true
    const store = heldStoreFor({ baseUrl, getToken, userId: user.id, sessionId, allowLocal: import.meta.env.DEV, workspaceId })
    const read = () => store.read().then(held => { if (live) setState({ loading: false, held }) })
    read()
    const unsubscribe = store.subscribe(() => { const held = store.peek(); if (live && held) setState({ loading: false, held }) })
    return () => { live = false; unsubscribe() }
  }, [getToken, user?.id, sessionId, isAuthenticated, workspaceId])
  return state
}

/**
 * Thin status line: sending on or off, last reply, unmatched replies, mailboxes needing attention, link to Machine Room.
 * `inline` sits in the top bar; `stacked` wraps for the phone menu.
 */
export function StatusStrip({ inline = false, className }) {
  const { role } = useRole()
  const { loading, status } = useShellStatus()
  const replies = useHeldReplies()
  const items = applyHeldToStrip(
    buildStripModel({ role, status, loading, machineRoomUrl: externalAddress({ env: 'VITE_MACHINE_ROOM_URL', fallback: null }, import.meta.env) }),
    { role, held: replies.held, loading: replies.loading },
  )
  return (
    <ul aria-label="Engine status" className={cn('flex items-center gap-2 list-none m-0 p-0', inline ? 'min-w-0 overflow-hidden whitespace-nowrap' : 'flex-wrap', className)}>
      {items.map(item => (
        <li key={item.key} className="shrink-0">
          {item.href ? (
            <a href={item.href} target="_blank" rel="noopener noreferrer" className={cn('inline-flex items-center gap-1 text-xs font-medium transition-colors duration-100', TONES.link)}>
              {item.label}
              <ExternalIcon width={12} height={12} strokeWidth={1.5} aria-hidden="true" />
            </a>
          ) : (
            <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', TONES[item.tone])}>{item.label}</span>
          )}
        </li>
      ))}
    </ul>
  )
}
